import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import AdminShell from "../AdminShell";

const API = process.env.REACT_APP_API_URL || "";

const ROLE_LABELS = { 0: "Investor", 1: "Admin", 2: "Manager", 3: "Agent" };
const ROLE_COLORS = { 0: "#10b981", 1: "#ef4444", 2: "#3b82f6", 3: "#f59e0b" };

const UsersRoles = () => {
  const { auth } = useSelector((s) => s);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const authHeader = { Authorization: `Bearer ${token}` };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API}/api/get_users`, { headers: authHeader })
      .then((r) => r.json())
      .then((d) => setUsers(Array.isArray(d) ? d : d.data || d.users || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const filtered = users.filter((u) => {
    const matchSearch =
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "all" || String(u.role) === roleFilter;
    return matchSearch && matchRole;
  });

  const handleRoleChange = async (userId, newRole) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/update_user/${userId}`, {
        method: "PUT",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ role: parseInt(newRole) }),
      });
      const d = await r.json();
      if (d.status === 1 || r.ok) {
        setUsers((prev) => prev.map((u) => u._id === userId ? { ...u, role: parseInt(newRole) } : u));
        setMsg({ type: "success", text: "Role updated successfully." });
      } else {
        setMsg({ type: "error", text: d.msg || "Failed to update role." });
      }
    } catch (e) {
      setMsg({ type: "error", text: "Network error." });
    } finally {
      setSaving(false);
      setEditUser(null);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  return (
    <AdminShell pageTitle="Users & Roles">
      <div style={{ maxWidth: 1100 }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>Users & Roles</h2>
          <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
            Manage platform users and their access roles.
          </p>
        </div>

        {msg && (
          <div style={{
            padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: msg.type === "success" ? "#d1fae5" : "#fee2e2",
            color: msg.type === "success" ? "#065f46" : "#991b1b",
            border: `1px solid ${msg.type === "success" ? "#6ee7b7" : "#fca5a5"}`,
          }}>
            {msg.text}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            style={{
              flex: 1, minWidth: 200, padding: "9px 14px", borderRadius: 8,
              border: "1px solid #e5e7eb", fontSize: 13, outline: "none",
            }}
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13, background: "#fff" }}
          >
            <option value="all">All Roles</option>
            <option value="0">Investor</option>
            <option value="1">Admin</option>
            <option value="2">Manager</option>
            <option value="3">Agent</option>
          </select>
        </div>

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                {["#", "Name", "Email", "Role", "KYC Status", "Joined", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Loading users…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>No users found.</td></tr>
              ) : filtered.map((u, idx) => (
                <tr key={u._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#9ca3af" }}>{idx + 1}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: "linear-gradient(135deg,#c9a84c,#7a5c10)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontWeight: 700, fontSize: 13, flexShrink: 0,
                      }}>
                        {(u.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{u.name || "—"}</span>
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>{u.email}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {editUser === u._id ? (
                      <select
                        defaultValue={u.role}
                        onChange={(e) => handleRoleChange(u._id, e.target.value)}
                        disabled={saving}
                        autoFocus
                        onBlur={() => setEditUser(null)}
                        style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #c9a84c", fontSize: 12 }}
                      >
                        <option value={0}>Investor</option>
                        <option value={1}>Admin</option>
                        <option value={2}>Manager</option>
                        <option value={3}>Agent</option>
                      </select>
                    ) : (
                      <span style={{
                        display: "inline-block", padding: "3px 10px", borderRadius: 20,
                        fontSize: 11, fontWeight: 700,
                        background: `${ROLE_COLORS[u.role] || "#6b7280"}18`,
                        color: ROLE_COLORS[u.role] || "#6b7280",
                      }}>
                        {ROLE_LABELS[u.role] ?? `Role ${u.role}`}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{
                      display: "inline-block", padding: "3px 10px", borderRadius: 20,
                      fontSize: 11, fontWeight: 700,
                      background: u.kycStatus === "approved" ? "#d1fae5" : u.kycStatus === "pending" ? "#fef3c7" : "#f3f4f6",
                      color: u.kycStatus === "approved" ? "#065f46" : u.kycStatus === "pending" ? "#92400e" : "#6b7280",
                    }}>
                      {u.kycStatus ? u.kycStatus.charAt(0).toUpperCase() + u.kycStatus.slice(1) : "Not Submitted"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "#9ca3af" }}>
                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "—"}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <button
                      onClick={() => setEditUser(u._id)}
                      style={{
                        padding: "5px 12px", borderRadius: 6, border: "1px solid #e5e7eb",
                        background: "#fff", fontSize: 12, cursor: "pointer", color: "#374151",
                        display: "flex", alignItems: "center", gap: 5,
                      }}
                    >
                      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit Role
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={{ marginTop: 12, fontSize: 12, color: "#9ca3af" }}>
          {filtered.length} user{filtered.length !== 1 ? "s" : ""} shown
        </p>
      </div>
    </AdminShell>
  );
};

export default UsersRoles;
